import type { Config, Player } from '@schibsted-svp/web-player';
import type { Asset } from '@schibsted-svp/svp-api-types';
import { Provider } from '@vgtv/api-client';
import { ConsentManager } from '@vgtv/consent-manager';

import {
  getAssetAspectRatio,
  getAssetMetadata,
} from '../../utils/player/asset';
import { getCategoryMetadataValue } from '../../utils/player/category';
import { getDeviceType } from '../../utils/device';
import type {
  AdnSiteConfig,
  DeviceType,
  PlacementType,
} from '../../config/adn/types';
import type {
  AdnContext,
  PlayerConfigOptions,
  Plugin,
} from '../../types/global';
import { generateXandrEids, getXandrPPIDs } from '../../config/adn/ppid';
import { getScreenOrientation } from '../../utils/screenOrientation';
import { prepareXandrKeywords } from '../../config/adn/prepareKeywords';
import { getClientKeywords } from '../../config/adn/vgtv/keywords';
import { getAdPermission } from '../../config/adn/getAdPermission';
import getUserId from '../../config/adn/getUserId';
import { getPlayerAspectRatio } from '../../config/adn/getPlayerAspectRatio';

import adsConfig from './config';

type SvpVmapParams = {
  supplyType: string;
  sdkType?: 'ima';
  site: string;
  forceAds?: boolean;
  bumper?: boolean;
  externalUid?: string;
  tcf_consent?: boolean;
  gdpr_consent?: string;
  eids?: string;
  client?: string;
};

interface AdnPluginConfig {
  options: PlayerConfigOptions;
  context: AdnContext;
}

const LONGFORM_DURATION = 2.5 * 60 * 1000; // 2.5 minutes in ms

const resolveIn =
  <T>(opts: { timeMs: number; defaultValue: T; defaultOnError?: boolean }) =>
  (promise: Promise<T>): Promise<T> => {
    const { timeMs, defaultValue, defaultOnError = true } = opts;
    return Promise.race([
      defaultOnError ? promise.catch(() => defaultValue) : promise,
      new Promise<T>((resolve) => {
        setTimeout(() => resolve(defaultValue), timeMs);
      }),
    ]);
  };

function getPropertyFromAssetOrCategory(
  asset: Asset | undefined,
  propertyName: string
) {
  return (
    getAssetMetadata(asset)[propertyName] ??
    getCategoryMetadataValue(asset?.category, propertyName)
  );
}

export interface AdnPlugin
  extends Plugin<AdnPlugin, { options: AdnPluginConfig }> {
  options: PlayerConfigOptions;
  context: AdnContext;
  skipAd: boolean;
  asset?: Asset;
  player: Player;

  getPlacement(placementType: PlacementType | 'pausead'): string | undefined;
  hasPlacement(placementType: PlacementType | 'pausead'): boolean;
  handleAssetReady(): void;
  getAsset(): Asset | undefined;
  getVmap: (params: {
    vendor: Provider;
    site: string;
    experimentMap: Record<string, string>;
    consentManager: ConsentManager;
  }) => Promise<any>;

  get muted(): boolean;
  get config(): Config;
  get siteConfig(): AdnSiteConfig<Provider.VGTV> | undefined;
  get deviceType(): DeviceType;
  get adsDisabled(): boolean;
  get prerollFormat(): 'preroll' | 'bumper' | 'sponsorroll';
  get pcFormat(): 'preroll' | 'bumper' | 'adfree' | 'skip';
  get prerollSubtype(): 'preroll' | 'bumper' | 'longform' | 'sponsorroll';
}

export const adnPlugin: AdnPlugin = {
  options: {} as any,
  player: undefined as any,
  context: {},
  asset: undefined,
  skipAd: false,

  getName() {
    return 'AdnPlugin';
  },

  initialize({ options: { options, context } }) {
    this.handleAssetReady = this.handleAssetReady.bind(this);
    this.options = options;
    this.context = context;
    // workaround for https://github.schibsted.io/svp/web-player/issues/1339
    context.adnPlugin = this;
    this.skipAd = options.noFirstAd ?? false;
  },

  setup() {
    this.context.player = this.player;
    this.listenTo(this.player, 'assetReady', this.handleAssetReady);
    this.handleAssetReady();
  },

  get siteConfig() {
    return adsConfig[this.options.site];
  },

  handleAssetReady() {
    const nextAsset = this.getAsset();

    if (this.skipAd && this.asset && this.asset.id !== nextAsset?.id) {
      this.skipAd = false;
    }

    this.asset = nextAsset;
  },

  getPlacement(placementType) {
    const {
      deviceType,
      options: { ads = {} },
    } = this;
    if (!ads[placementType]) {
      return undefined;
    }

    return this.siteConfig?.placements?.[placementType]?.[deviceType];
  },

  hasPlacement(placementType) {
    return Boolean(this.getPlacement(placementType));
  },

  getAsset() {
    const asset = this.player.getRawAsset();
    if (
      asset &&
      asset.streamType !== ('mock' as unknown as Asset['streamType'])
    ) {
      return asset;
    }
    return undefined;
  },

  get deviceType() {
    const deviceType = getDeviceType();
    return deviceType === 'mobile' ? 'phone' : deviceType;
  },

  get adsDisabled() {
    return (
      this.config.settings?.na ||
      this.skipAd ||
      this.asset?.additional?.settings?.showAds === false
    );
  },

  get muted() {
    return Boolean(this.player.getMute() ?? this.config.mute);
  },

  get config() {
    return this.player.getRawConfig();
  },

  get prerollFormat() {
    if (
      (getPropertyFromAssetOrCategory(this.asset, 'sponsorroll') === 'true' ||
        this.asset?.additional?.tags?.includes(
          'fce06749-ff57-406d-85e5-c6ff92ff6e29' // VG Lista
        )) &&
      this.hasPlacement('sponsorroll')
    ) {
      return 'sponsorroll';
    }

    if (this.muted && this.hasPlacement('bumper')) {
      return 'bumper';
    }

    return 'preroll';
  },

  get pcFormat() {
    const { prerollFormat, adsDisabled } = this;
    if (prerollFormat === 'bumper') {
      return adsDisabled ? 'skip' : 'bumper';
    }

    return adsDisabled ? 'adfree' : 'preroll';
  },

  get prerollSubtype() {
    const { prerollFormat } = this;

    const customType = getPropertyFromAssetOrCategory(
      this.asset,
      'forcePrerollPlacementType'
    )?.trim() as 'preroll' | 'longform' | 'sponsorroll' | undefined;

    if (customType && this.hasPlacement(customType)) {
      return customType;
    }

    if (prerollFormat === 'preroll') {
      const { duration = 0 } = this.asset || {};
      if (duration > LONGFORM_DURATION && this.hasPlacement('longform')) {
        return 'longform';
      }
    }

    return prerollFormat;
  },

  async getVmap({ vendor, site, experimentMap, consentManager }) {
    const { ppid1, ppid2 } = await resolveIn({
      timeMs: 1500,
      defaultValue: {} as ReturnType<typeof getXandrPPIDs>,
    })(
      new Promise((resolve) => {
        // TODO: hacky way of fetching pulse and eids
        // @ts-expect-error meh
        window.pulse(`svp-tracker-${vendor}`, (tracker) => {
          resolve(getXandrPPIDs(tracker));
        });
      })
    );

    const dynamicAR =
      experimentMap['ads-aspect-ratio'] === 'true' &&
      getScreenOrientation() === 'portrait';

    const keywords = prepareXandrKeywords(
      getClientKeywords(
        dynamicAR,
        getAssetAspectRatio(this.asset),
        getPlayerAspectRatio(this.player)
      )
    );

    const params: SvpVmapParams = {
      supplyType: `web_${getDeviceType()}`,
      site,
      bumper: this.prerollFormat === 'bumper',
      client: encodeURIComponent(
        new URLSearchParams(keywords as Record<string, string>).toString()
      ),
      tcf_consent: (await getAdPermission(consentManager)) === '1',
      externalUid: getUserId(),
      eids: generateXandrEids(vendor, ppid1, ppid2)
        .map(({ source, id }) => `${source},${id}`)
        .join('|'),
    };
    return Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== false && v !== '')
      .reduce((aggr, [k, v]) => ({ ...aggr, [k]: String(v) }), {});
  },
};

window.SVP.Player.Plugins.register(adnPlugin);
